import { runConnectorQuery, withSkip } from './transport';

import type {
	ConnectorPage,
	ConnectorQueryEcho,
	ConnectorResource,
	ExecuteConnectorQuery,
	RunConnectorQuery,
} from './types';

/**
 * Pagination math for the connector, with the transport injected.
 *
 * Restored from the declined spike (commit 8ff2d69fbd,
 * `data-resource-query-executor.ts`). The spike's transport held a hardcoded
 * product-template query, which is why its own commit message said "query is
 * product-template-specific". This version replays the echoed query instead, so
 * the filter and the sort of page two match page one.
 */
export const createExecuteConnectorQuery = (
	run: RunConnectorQuery = runConnectorQuery,
): ExecuteConnectorQuery => {
	return async (echo: ConnectorQueryEcho): Promise<ConnectorResource> => {
		const page = await run(echo);
		return { items: page.items, query: buildNextQuery(echo, page) };
	};
};

/**
 * The echo for the next page, or `null` at the end.
 *
 * `total` comes from the collection node. The spike inferred "more pages exist"
 * from a non-empty page when `total` was missing, and the review bot flagged it,
 * because that makes every non-empty page look like it has a successor. The
 * connector always returns a numeric `total`, so read it.
 */
export const buildNextQuery = (
	echo: ConnectorQueryEcho,
	page: ConnectorPage,
): ConnectorQueryEcho | null => {
	if (page.items.length === 0) return null;
	const nextSkip = page.skip + page.items.length;
	if (nextSkip >= page.total) return null;
	return withSkip(echo, nextSkip);
};
